import { useRef, useEffect, useLayoutEffect, useCallback, useState, forwardRef, useImperativeHandle } from "react";
import { cn } from "@/lib/utils";
import { BASE_TEXT_FONT_SIZE } from "./types";

export interface InlineTextEditorHandle {
    confirmOrCancel: () => void;
}

interface InlineTextEditorProps {
    color: string;
    initialText?: string;
    /** Ref that, when true, suppresses blur-to-confirm (e.g. while using color slider) */
    suppressBlurRef?: React.RefObject<boolean>;
    onConfirm: (text: string) => void;
    onCancel: () => void;
}

export const InlineTextEditor = forwardRef<InlineTextEditorHandle, InlineTextEditorProps>(function InlineTextEditor({
    color,
    initialText = "",
    suppressBlurRef,
    onConfirm,
    onCancel,
}, ref) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const confirmedRef = useRef(false);
    const [hasText, setHasText] = useState(initialText.length > 0);
    const [keyboardHeight, setKeyboardHeight] = useState(0);

    // Lock body scroll to prevent iOS keyboard from shifting the viewport
    useEffect(() => {
        document.body.classList.add('keyboard-lock');
        return () => {
            document.body.classList.remove('keyboard-lock');
        };
    }, []);

    // Track keyboard height via visualViewport API
    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return;

        const handleResize = () => {
            const kbHeight = window.innerHeight - vv.height;
            setKeyboardHeight(kbHeight > 0 ? kbHeight : 0);
        };

        // Check initial state (keyboard may already be shown via proxy input)
        handleResize();

        vv.addEventListener('resize', handleResize);
        return () => vv.removeEventListener('resize', handleResize);
    }, []);

    // Size textarea and transfer focus before first paint.
    // The parent focuses a proxy input during the tap gesture to raise the mobile keyboard;
    // transferring focus here keeps the keyboard open.
    useLayoutEffect(() => {
        resizeTextarea();
        const ta = textareaRef.current;
        if (ta) {
            ta.focus({ preventScroll: true });
            ta.selectionStart = ta.value.length;
            ta.selectionEnd = ta.value.length;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Resize textarea to fit content
    const resizeTextarea = useCallback(() => {
        const ta = textareaRef.current;
        const measure = measureRef.current;
        if (!ta || !measure) return;

        // Mirror the text into the hidden measurer to get content width
        // Replace spaces with non-breaking spaces so trailing spaces are measured
        const text = ta.value || ta.placeholder;
        measure.textContent = text.replace(/ /g, "\u00a0");

        // Width: use measurer's scrollWidth + padding for the cursor, clamped to min
        const contentWidth = measure.scrollWidth;
        // Add some padding for the cursor and rounding
        const newWidth = contentWidth + 12;
        ta.style.width = `${newWidth}px`;

        // Height: auto-grow
        ta.style.height = "auto";
        ta.style.height = `${ta.scrollHeight}px`;

        setHasText(ta.value.length > 0);
    }, []);

    const handleInput = useCallback(() => {
        resizeTextarea();
    }, [resizeTextarea]);

    const doConfirmOrCancel = useCallback(() => {
        if (confirmedRef.current) return;
        confirmedRef.current = true;
        const ta = textareaRef.current;
        const text = ta?.value.trim() ?? "";
        if (text) {
            onConfirm(text);
        } else {
            onCancel();
        }
    }, [onConfirm, onCancel]);

    useImperativeHandle(ref, () => ({
        confirmOrCancel: doConfirmOrCancel,
    }), [doConfirmOrCancel]);

    // Tap outside the textarea -> confirm or cancel
    const handleBackdropPointerDown = useCallback(
        (e: React.PointerEvent) => {
            if (e.target === e.currentTarget) {
                e.stopPropagation();
                e.preventDefault();
                doConfirmOrCancel();
            }
        },
        [doConfirmOrCancel]
    );

    // Blur also confirms — unless suppressed (e.g. color slider interaction)
    const handleBlur = useCallback(() => {
        setTimeout(() => {
            if (suppressBlurRef?.current) {
                // Re-focus the textarea after the external interaction
                textareaRef.current?.focus({ preventScroll: true });
                return;
            }
            doConfirmOrCancel();
        }, 100);
    }, [doConfirmOrCancel, suppressBlurRef]);

    return (
        <div
            className="absolute inset-0 z-20 flex items-center justify-center bg-black/50"
            onPointerDown={handleBackdropPointerDown}
            style={{
                touchAction: "none",
                paddingBottom: keyboardHeight > 0 ? keyboardHeight : undefined,
            }}
        >
            <div
                className="max-w-[80%] flex justify-center"
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
            >
                {/* Hidden measurer — must match textarea font/size/padding exactly */}
                <span
                    ref={measureRef}
                    aria-hidden
                    style={{
                        position: "absolute",
                        visibility: "hidden",
                        height: 0,
                        overflow: "hidden",
                        whiteSpace: "pre",
                        fontSize: BASE_TEXT_FONT_SIZE,
                        fontWeight: "bold",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        lineHeight: 1.4,
                        padding: `${BASE_TEXT_FONT_SIZE * 0.25}px ${BASE_TEXT_FONT_SIZE * 0.4}px`,
                    }}
                />
                <textarea
                    ref={textareaRef}
                    defaultValue={initialText}
                    onInput={handleInput}
                    onBlur={handleBlur}
                    rows={1}
                    style={{
                        fontSize: BASE_TEXT_FONT_SIZE,
                        fontWeight: "bold",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        lineHeight: 1.4,
                        borderRadius: BASE_TEXT_FONT_SIZE * 0.6,
                        padding: `${BASE_TEXT_FONT_SIZE * 0.25}px ${BASE_TEXT_FONT_SIZE * 0.4}px`,
                        color: color,
                        caretColor: color,
                        minWidth: 0,
                    }}
                    className={cn(
                        "text-center resize-none overflow-hidden",
                        "border-none outline-none",
                        hasText
                            ? "bg-white/90"
                            : "bg-transparent placeholder:text-white/50",
                    )}
                    placeholder="Add text"
                />
            </div>
        </div>
    );
});
